import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { getAllOrderProgressItems, reorderOrderProgressItems } from '../redux/OrderProgressCRUD';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import {
  useModuleToolbarPermissions,
  isTabVisibleForToolbarCompany,
} from '@/__core/permissions';
import { TabData } from '@/__core/types/TabData';
import MainListModule from '@/__core/layout/module/MainListModule';
import AppListToolbarGlobal from '@/modules/order-progress/components/OrderProgressToolbarGlobal';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import MessageSnackbar from '@/__core/helpers/components/MessageSnackbar';
import { formatErrorForSnackbar } from '@/__core/helpers/Error';
import { useGlobalLoading } from '@/__core/hooks/useGlobalLoading';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import {
  resolveActiveCompanyFilter,
  resolveToolbarTabCompanyFilter,
} from '@/__core/hooks/useCompanyContext';
import type { OrderProgressCreatedItem, OrderProgressItem } from '../models/OrderProgressModel';
import { getDateKey } from '../utils/orderProgressDateUtils';
import {
  buildOrderProgressCopySource,
  buildOrderProgressCopyTabTitle,
} from '../utils/buildOrderProgressCopySource';

import MainListOrderProgress from './MainListOrderProgress';
import NewOrderProgress from './NewOrderProgress';

const toProgressItem = (created: OrderProgressCreatedItem): OrderProgressItem => ({
  id: created.id,
  date: created.date,
  employee_ids: created.employee_ids,
  employee_names: created.employee_names || [],
  note: created.note,
  order_id: created.order_id,
  order_code: created.order_code || '',
  customer_name: created.customer_name,
  sales_employee_name: created.sales_employee_name,
  delivery_address_line: created.delivery_address_line,
  created_at: created.created_at || new Date().toISOString(),
  updated_at: created.updated_at,
  created_by_name: created.created_by_name,
  updated_by_name: created.updated_by_name,
});

const getCopySourceFromTab = (tab: TabData): OrderProgressItem | null => {
  const copyFrom = tab.metadata?.copyFrom as OrderProgressItem | undefined;
  return copyFrom || null;
};

export default function OrderProgressModule() {
  const { startGlobalLoading, stopGlobalLoading } = useGlobalLoading();

  const [filters, setFilters] = useState({ company: 'all' });
  const [progressItems, setProgressItems] = useState<OrderProgressItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const selectedCompanyId = getCurrentCompanyId() || '';
  const activeCompanyFilter = resolveActiveCompanyFilter(selectedCompanyId, filters.company);
  const toolbarTabCompanyFilter = resolveToolbarTabCompanyFilter(filters.company);

  const {
    canRead,
    canCreate,
    canUpdate,
    resolveTabPermissions,
  } = useModuleToolbarPermissions({
    resource: 'orders.orderprogress',
    activeCompanyFilter,
    updateTabTypes: ['update-order-progress'],
  });

  const defaultCompanyForNewProgress = useMemo(() => {
    if (toolbarTabCompanyFilter !== 'all') {
      return toolbarTabCompanyFilter;
    }
    return String(selectedCompanyId || '').trim() || undefined;
  }, [selectedCompanyId, toolbarTabCompanyFilter]);

  const defaultTabs: TabData[] = useMemo(() => {
    const tabs: TabData[] = [
      {
        id: 0,
        title: 'Danh Sách Tiến Độ Đơn Hàng',
        type: 'list',
        closable: false,
        createdAt: Date.now(),
      },
    ];
    if (canCreate) {
      tabs.push({ id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() });
    }
    return tabs;
  }, [canCreate]);

  const {
    storedTabs,
    activeTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    openTab,
    updateTab,
    removeTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'order_progress_module_tabs',
    defaultTabs,
    maxTabs: 20,
    canCreate: canCreate === true,
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-order-progress', 'update-order-progress'],
    allowMultipleTabTypes: ['new-order-progress', 'update-order-progress'],
    buildNewTab: (currentTabs) => {
      const nextNumber = currentTabs.filter((tab) => tab.type === 'new-order-progress').length + 1;
      const companyId = defaultCompanyForNewProgress;
      return {
        id: Date.now(),
        title: `Tiến độ #${nextNumber}`,
        type: 'new-order-progress',
        closable: true,
        entityType: 'order-progress',
        metadata: { mode: 'create', companyId },
      };
    },
    onAfterTabChange: () => {
      setSuccessMessage(null);
      setErrorMessage(null);
    },
  });

  const loadProgressItems = useCallback(async () => {
    if (!canRead) {
      return;
    }

    setLoading(true);
    const loadingKey = 'load-progress-items';
    startGlobalLoading('Đang tải dữ liệu...', loadingKey);

    try {
      const requestFilters: Record<string, string> = {};

      if (searchQuery) {
        requestFilters.search = searchQuery;
      }
      if (activeCompanyFilter && activeCompanyFilter !== 'all') {
        requestFilters.company_id = activeCompanyFilter;
      }

      const items = await getAllOrderProgressItems(requestFilters);

      const sortedItems = items.sort((a: OrderProgressItem, b: OrderProgressItem) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
      });

      setProgressItems(sortedItems);
    } catch (error: unknown) {
      setErrorMessage(formatErrorForSnackbar(error as Error) || 'Không thể tải dữ liệu tiến độ');
    } finally {
      setLoading(false);
      stopGlobalLoading(loadingKey);
    }
  }, [activeCompanyFilter, canRead, searchQuery, startGlobalLoading, stopGlobalLoading]);

  useEffect(() => {
    loadProgressItems();
  }, [loadProgressItems]);

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    if (value === '') {
      setSearchQuery('');
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInputValue);
  };

  const handleFilterChange = useCallback((field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleOpenEditTab = useCallback(
    (item: OrderProgressItem) => {
      if (!canUpdate) {
        setErrorMessage('Bạn không có quyền cập nhật tiến độ đơn hàng');
        return;
      }

      const companyId =
        toolbarTabCompanyFilter !== 'all' ? toolbarTabCompanyFilter : undefined;

      openTab({
        id: Date.now(),
        title: item.order_code || 'Tiến độ đơn hàng',
        type: 'update-order-progress',
        closable: true,
        entityId: item.id,
        entityType: 'order-progress',
        entityData: item,
        metadata: { mode: 'edit', companyId },
      });
    },
    [canUpdate, openTab, toolbarTabCompanyFilter],
  );

  const handleOpenCopyTab = useCallback(
    (item: OrderProgressItem) => {
      if (!canCreate) {
        setErrorMessage('Bạn không có quyền tạo tiến độ đơn hàng');
        return;
      }

      const copySource = buildOrderProgressCopySource(item);
      const nextNumber = storedTabs.filter((tab) => tab.type === 'new-order-progress').length + 1;

      const companyId = defaultCompanyForNewProgress;

      openTab({
        id: Date.now(),
        title: buildOrderProgressCopyTabTitle(copySource, nextNumber),
        type: 'new-order-progress',
        closable: true,
        entityType: 'order-progress',
        metadata: { mode: 'create', copyFrom: copySource, companyId },
      });
    },
    [canCreate, defaultCompanyForNewProgress, openTab, storedTabs],
  );

  const handleCancelProgressTab = useCallback(
    (tab: TabData) => {
      removeTab(tab.id);
    },
    [removeTab],
  );

  const handleProgressCreated = useCallback(
    (createdItem: OrderProgressCreatedItem, tabId: number) => {
      const fullItem = toProgressItem(createdItem);
      updateTab(tabId, {
        title: fullItem.order_code || 'Tiến độ đơn hàng',
        type: 'update-order-progress',
        entityId: fullItem.id,
        entityData: fullItem,
        metadata: {
          mode: 'edit',
          companyId:
            toolbarTabCompanyFilter !== 'all' ? toolbarTabCompanyFilter : undefined,
        },
      });
      void loadProgressItems();
      setSuccessMessage('Tạo mới tiến độ thành công!');
    },
    [loadProgressItems, toolbarTabCompanyFilter, updateTab],
  );

  const handleReorderItems = async (dateKey: string, newOrder: OrderProgressItem[]) => {
    if (!canUpdate) {
      setErrorMessage('Bạn không có quyền cập nhật tiến độ đơn hàng');
      return;
    }

    const previousItems = [...progressItems];

    setProgressItems((prevItems) => {
      const itemsNotInDateGroup = prevItems.filter((item) => getDateKey(item.date) !== dateKey);

      const updatedItems = newOrder.map((item, index) => ({
        ...item,
        order_index: index,
      }));

      const allItems = [...itemsNotInDateGroup, ...updatedItems];

      return allItems.sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;

        if (dateA !== dateB) {
          return dateB - dateA;
        }

        const aDateKey = getDateKey(a.date);
        const bDateKey = getDateKey(b.date);
        if (aDateKey === dateKey && bDateKey === dateKey) {
          const indexA = a.order_index ?? 999999;
          const indexB = b.order_index ?? 999999;
          return indexA - indexB;
        }

        const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return timeB - timeA;
      });
    });

    try {
      const reorderData = newOrder.map((item, index) => ({
        id: item.id,
        order_index: index,
      }));

      await reorderOrderProgressItems(reorderData);
    } catch (error: unknown) {
      setProgressItems(previousItems);
      setErrorMessage(formatErrorForSnackbar(error as Error) || 'Không thể cập nhật thứ tự');
    }
  };

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={storedTabs[activeTab]?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType="orders"
        unsavedDialog={unsavedDialog}
      />

      <MessageSnackbar
        open={!!successMessage}
        message={successMessage || ''}
        severity="success"
        onClose={() => setSuccessMessage(null)}
      />

      <MessageSnackbar
        open={!!errorMessage}
        message={errorMessage || ''}
        severity="error"
        onClose={() => setErrorMessage(null)}
      />

      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
          <Box sx={{ position: 'relative', width: '100%', minWidth: 0, overflow: 'hidden' }}>
            {storedTabs.map((tab) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }

              const isActive = isStoredTabPanelActive(tab);
              const tabPermissions = resolveTabPermissions(tab);

              return (
                <Box
                  key={tab.id}
                  sx={{
                    display: isActive ? 'flex' : 'none',
                    width: '100%',
                    height: '100%',
                    flexDirection: 'column',
                    minWidth: 0,
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  {tab.type === 'list' ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        width: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                      }}
                    >
                      {!canRead ? (
                        <Box sx={{ p: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            Bạn không có quyền xem danh sách tiến độ đơn hàng.
                          </Typography>
                        </Box>
                      ) : (
                        <MainListModule
                          leftSidebar={null}
                          toolbar={
                            <AppListToolbarGlobal
                              searchQuery={searchInputValue}
                              onSearchChange={handleSearchInputChange}
                              onSearchSubmit={handleSearchSubmit}
                              filters={filters}
                              onFilterChange={handleFilterChange}
                            />
                          }
                          dataTable={
                            <MainListOrderProgress
                              progressItems={progressItems}
                              loading={loading}
                              onRowClick={handleOpenEditTab}
                              onCopyClick={handleOpenCopyTab}
                              onUpdate={loadProgressItems}
                              onReorderItems={handleReorderItems}
                              canCreate={canCreate}
                              canUpdate={canUpdate}
                              onSuccess={setSuccessMessage}
                            />
                          }
                        />
                      )}
                    </Box>
                  ) : tab.type === 'new-order-progress' ? (
                    <NewOrderProgress
                      key={`new-order-progress-${tab.id}`}
                      mode="create"
                      item={getCopySourceFromTab(tab)}
                      tabId={tab.id}
                      isActive={isActive}
                      onCancel={() => handleCancelProgressTab(tab)}
                      onUpdate={loadProgressItems}
                      onItemCreated={(created) => handleProgressCreated(created, tab.id)}
                      onSuccess={setSuccessMessage}
                      disabled={!tabPermissions.canCreate}
                      onUnsavedChanges={handleUnsavedChangesUpdate}
                      activeCompanyFilter={activeCompanyFilter}
                    />
                  ) : tab.type === 'update-order-progress' ? (
                    <NewOrderProgress
                      key={`update-order-progress-${tab.id}`}
                      mode="edit"
                      item={(tab.entityData as OrderProgressItem) || null}
                      tabId={tab.id}
                      isActive={isActive}
                      onCancel={() => handleCancelProgressTab(tab)}
                      onUpdate={loadProgressItems}
                      onSuccess={setSuccessMessage}
                      disabled={!tabPermissions.canUpdate}
                      onUnsavedChanges={handleUnsavedChangesUpdate}
                      activeCompanyFilter={activeCompanyFilter}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </>
  );
}
